import { Button, CircularProgress } from '@civilgrid/ui/components';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { join } from '~/api/auth2';
import { useQuery } from '~/common/routerUtils';
import Checkbox from '~/components2/forms/Checkbox';
import { emailRegex } from '~/components2/forms/common';
import Form from '~/components2/forms/Form';
import Input from '~/components2/forms/Input';
import PageLayout from './PageLayout';
import store from './store';

interface OrgInvite {
  inviteType: 'organization';
  organizationName: string;
}

interface ProjectInvite {
  inviteType: 'project';
  projectName: string;
}

interface InviteDetails {
  inviteeEmail: string;
  inviterName: string;
}

type Invite = InviteDetails & (OrgInvite | ProjectInvite);

interface JoinForm {
  firstName: string;
  lastName: string;
  email: string;
  agreedTerms: boolean;
}

export default function JoinComponent() {
  const navigate = useNavigate();
  const query = useQuery();
  const encodedInvite = query.get('invite');
  const [invite, setInviteDetails] = useState<Invite | undefined>();

  const { handleSubmit, control, setValue, formState } = useForm<JoinForm>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      agreedTerms: false,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!encodedInvite && !invite) {
      navigate({ to: '/register' });
    } else {
      // the URL search params disappear in the query after initial load.
      // only set the values if the encoded invite contains valid data.
      if (encodedInvite) {
        const _invite: Invite = JSON.parse(atob(encodedInvite));
        setInviteDetails(_invite);
        setValue('email', _invite.inviteeEmail);
      }
    }
  }, [encodedInvite]);

  const invitePrompt = useMemo(() => {
    if (invite?.inviteType === 'project') {
      return (
        <span>
          Join {invite.inviterName} on <span className="font-bold">{invite.projectName}</span>!
        </span>
      );
    } else if (invite?.inviteType === 'organization') {
      return (
        <span>
          Join {invite.inviterName} at <span className="font-bold">{invite.organizationName}</span>!
        </span>
      );
    } else {
      return null;
    }
  }, [invite]);

  const onFormSubmit = async ({ firstName, lastName, email }: JoinForm) => {
    try {
      // setting it here so it can be accessed from login/authCode at the next step
      store.email = email;
      const data = await join({ email, firstName, lastName });
      if (data.redirect) {
        if (data.redirect.startsWith('http')) {
          window.location.href = data.redirect;
        } else {
          navigate({ to: data.redirect });
        }
      } else {
        navigate({ to: '/login/authCode' });
      }
    } catch (e) {
      alert('Join failed, please contact support@civilgrid.com');
      console.error(e);
    }
  };

  if (invite) {
    return (
      <PageLayout data-testid="join-page">
        <div className="my-2 text-center text-lg">{invitePrompt}</div>
        <Form className="space-y-2" onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="firstName"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                label="First Name"
                placeholder="Alice"
                {...field}
                error={error?.message}
                data-testid="join-first-name-input"
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                label="Last Name"
                placeholder="Appleseed"
                {...field}
                error={error?.message}
                data-testid="join-last-name-input"
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Please enter email',
              pattern: {
                value: emailRegex,
                message: 'Invalid email',
              },
            }}
            render={({ field }) => (
              <Input
                label="Email"
                placeholder="alice@acme-engineers.com"
                {...field}
                disabled
                data-testid="join-email-input"
              />
            )}
          />
          <Controller
            name="agreedTerms"
            control={control}
            rules={{
              required: 'Agree to terms',
            }}
            render={({ field, fieldState: { error } }) => (
              <Checkbox
                checked={field.value}
                label={
                  <span className="text-xs">
                    I agree to the&nbsp;
                    <a
                      href="https://drive.google.com/uc?export=download&id=1ivF-Bl1Hcxqj6SLytM9RlhiBOte9CBO1"
                      target="_blank"
                      download="CivilGridTermsOfUse.pdf"
                      rel="noreferrer noopener"
                      className="text-blue-50 underline hover:text-blue-60"
                    >
                      CivilGrid Terms of Use
                    </a>
                    &nbsp;and&nbsp;
                    <a
                      href="https://drive.google.com/uc?export=download&id=1XciSJ0JtW0IVyjItiDSPgS2FkmGKMkeJ"
                      target="_blank"
                      download="CivilGridPrivacyNotice.pdf"
                      rel="noreferrer noopener"
                      className="text-blue-50 underline hover:text-blue-60"
                    >
                      CivilGrid Privacy Notice
                    </a>
                    .
                  </span>
                }
                {...field}
              />
            )}
          />
          <Button type="submit" disabled={!formState.isValid} data-testid="join-submit-button">
            Join
          </Button>
        </Form>
        <div className="mt-2 text-xs">
          Already have an account?{' '}
          <Link className="text-blue-50 underline hover:text-blue-60" to="/login" data-testid="join-login-link">
            Login
          </Link>
        </div>
      </PageLayout>
    );
  } else {
    return <CircularProgress indeterminate />;
  }
}
